import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Header from '../components/Header';
import { toast } from 'sonner';

const BookingSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      navigate('/locations');
      return;
    }
    
    checkPaymentStatus(sessionId);
  }, [searchParams, navigate]);

  const checkPaymentStatus = async (sessionId) => {
    let attempts = 0;
    const maxAttempts = 5;
    
    const poll = async () => {
      if (attempts >= maxAttempts) {
        setChecking(false);
        toast.error('Kontrola platby trvá příliš dlouho');
        return;
      }
      
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
        const response = await fetch(`${API_URL}/payments/checkout-status/${sessionId}`);
        
        if (!response.ok) {
          throw new Error('Chyba při kontrole platby');
        }
        
        const data = await response.json();
        
        if (data.payment_status === 'paid') {
          setPaymentStatus('success');
          setChecking(false);
          return;
        }
        
        if (data.status === 'expired') {
          setPaymentStatus('expired');
          setChecking(false);
          return;
        }
        
        // Continue polling
        attempts++;
        setTimeout(poll, 2000);
      } catch (error) {
        console.error('Payment check error:', error);
        setChecking(false);
        toast.error('Chyba při kontrole platby');
      }
    };
    
    poll();
  };

  return (
    <div 
      className="min-h-screen"
    >
      <Header />
      
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-32 sm:pt-44 lg:pt-52 pb-20">
        <Card className="shadow-2xl">
          <CardContent className="p-12 text-center">
            {checking && (
              <>
                <Loader2 className="w-16 h-16 text-orange-600 mx-auto mb-6 animate-spin" />
                <h1 className="text-3xl font-bold mb-4">Kontroluji platbu...</h1>
                <p className="text-gray-600">Prosím čekejte, ověřujeme vaši platbu.</p>
              </>
            )}
            
            {!checking && paymentStatus === 'success' && (
              <>
                <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-6" />
                <h1 className="text-3xl font-bold mb-4">Platba úspěšná!</h1>
                <p className="text-gray-600 mb-8">
                  Vaše rezervace byla potvrzena. Email s potvrzením vám byl odeslán.
                </p>
                <div className="flex gap-4 justify-center">
                  <Button onClick={() => navigate('/dashboard')} className="bg-orange-600 hover:bg-orange-700">
                    Zobrazit rezervace
                  </Button>
                  <Button onClick={() => navigate('/locations')} variant="outline">
                    Prozkoumat další místa
                  </Button>
                </div>
              </>
            )}
            
            {!checking && paymentStatus === 'expired' && (
              <>
                <h1 className="text-3xl font-bold mb-4">Platba vypršela</h1>
                <p className="text-gray-600 mb-8">Platební session vypršela. Zkuste to prosím znovu.</p>
                <Button onClick={() => navigate('/locations')} className="bg-orange-600 hover:bg-orange-700">
                  Zpět na místa
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BookingSuccess;
